import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import CommandAnalyticsStrip from '../components/analytics/CommandAnalyticsStrip';
import { AreaTrendChart, HorizontalMeters } from '../components/analytics/CommandCharts';
import api from '../api';
import {
  buildNotificationLevelRows,
  buildNotificationTrendRows,
  buildSupportStatusRows,
  buildSupportTrendRows,
  isActiveSupportCase,
  isStaleSupportCase,
} from '../utils/supportSignals';

const EMPTY_STATS = {
  totalOrganizations: 0,
  activeOrganizations: 0,
  totalSystems: 0,
  warningSystems: 0,
  openRenewals: 0,
  oauthClients: 0,
  openSupportCases: 0,
  openSocialReports: 0,
  pendingSystems: 0,
  pendingFriendRequests: 0,
  activeSocialUsers: 0,
  totalCommunitySpaces: 0,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [attentionSystems, setAttentionSystems] = useState([]);
  const [recentOrganizations, setRecentOrganizations] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [supportCases, setSupportCases] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [commandSummary, setCommandSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingCommand, setRefreshingCommand] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    Promise.all([
      api.get('/users/profile', { headers: { Authorization: `Bearer ${token}` } }),
      api.get('/admin/stats', { headers: { Authorization: `Bearer ${token}` } }),
      api.get('/admin/platform/overview', { headers: { Authorization: `Bearer ${token}` } }),
      api.get('/admin/platform/command-analytics', { headers: { Authorization: `Bearer ${token}` } }),
      api.get('/admin/platform/support-cases', { headers: { Authorization: `Bearer ${token}` } }),
      api.get('/admin/platform/notifications', { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([profileRes, statsRes, overviewRes, commandRes, supportRes, notificationsRes]) => {
        setUser(profileRes.data.user);
        setStats({
          ...EMPTY_STATS,
          ...statsRes.data,
          ...(overviewRes.data.stats || {}),
        });
        setAttentionSystems(overviewRes.data.attentionSystems || []);
        setRecentOrganizations(overviewRes.data.recentOrganizations || []);
        setRecentActivity(overviewRes.data.recentActivity || []);
        setCommandSummary(commandRes.data || null);
        setSupportCases(supportRes.data.supportCases || []);
        setNotifications(notificationsRes.data.notifications || []);
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Could not load dashboard data');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const refreshCommandAnalytics = async () => {
    setRefreshingCommand(true);
    try {
      const { data } = await api.post('/admin/platform/command-analytics/refresh', {
        range: '24h',
      });
      setCommandSummary(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not refresh command analytics');
    } finally {
      setRefreshingCommand(false);
    }
  };

  const supportTrend = useMemo(() => buildSupportTrendRows(supportCases, 7), [supportCases]);
  const supportStatusRows = useMemo(
    () =>
      buildSupportStatusRows(supportCases).map((row) => ({
        ...row,
        color: row.label === 'Resolved' || row.label === 'Closed' ? '#7ED957' : '#FFCC00',
        accent: row.label === 'Resolved' || row.label === 'Closed' ? '#42B95C' : '#FF6D00',
      })),
    [supportCases]
  );
  const staleSupportCases = useMemo(
    () => supportCases.filter((item) => isStaleSupportCase(item)).length,
    [supportCases]
  );
  const activeSupportCases = useMemo(
    () => supportCases.filter(isActiveSupportCase).length,
    [supportCases]
  );
  const notificationTrend = useMemo(
    () => buildNotificationTrendRows(notifications, 7),
    [notifications]
  );
  const notificationLevelRows = useMemo(
    () =>
      buildNotificationLevelRows(notifications).map((row) => ({
        ...row,
        color:
          row.label === 'Critical'
            ? '#FF5D73'
            : row.label === 'Warning'
              ? '#FFCC00'
              : row.label === 'Success'
                ? '#7ED957'
                : '#67B7FF',
        accent:
          row.label === 'Critical'
            ? '#FF2446'
            : row.label === 'Warning'
              ? '#FF6D00'
              : row.label === 'Success'
                ? '#42B95C'
                : '#2F83FF',
      })),
    [notifications]
  );
  const escalatedNotifications = useMemo(
    () =>
      notifications.filter((item) =>
        ['warning', 'critical'].includes(String(item?.level || '').toLowerCase())
      ).length,
    [notifications]
  );

  if (loading) {
    return (
      <Layout>
        <StateCard message="Loading command center..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <StateCard message={error} tone="error" />
      </Layout>
    );
  }

  const controlLanes = [
    {
      title: 'Command alerts',
      body: 'Cross-product automation warnings, stale sources, and renewal pressure now have their own page and workflow.',
      value: commandSummary?.freshness?.staleSourceCount || commandSummary?.externalSummary?.warningSources || 0,
      meta: 'Ops rules + escalation',
      to: '/command-alerts',
    },
    {
      title: 'Business portfolio',
      body: 'Manage live organizations, profile readiness, and support risk without crowding the dashboard.',
      value: stats.totalOrganizations,
      meta: `${stats.activeOrganizations || 0} active`,
      to: '/organizations',
    },
    {
      title: 'Systems watch',
      body: 'Jump into connected products, health posture, and renewal pressure from a dedicated workspace.',
      value: stats.warningSystems,
      meta: `${stats.totalSystems} tracked`,
      to: '/systems',
    },
    {
      title: 'Support inbox',
      body: 'Customer cases now stay on their own page instead of sharing the same queue with moderation.',
      value: stats.openSupportCases,
      meta: 'Support only',
      to: '/support',
    },
    {
      title: 'Safety desk',
      body: 'Moderation reports and enforcement actions now live in their own workflow.',
      value: stats.openSocialReports,
      meta: `${stats.pendingFriendRequests || 0} pending requests`,
      to: '/safety',
    },
    {
      title: 'Verification desk',
      body: 'Handle retries, appeals, and manual review without losing focus in the wider admin.',
      value: stats.oauthClients,
      meta: 'Identity + access',
      to: '/verification',
    },
    {
      title: 'Growth pulse',
      body: 'Retention, community activity, and engagement signals have their own page now.',
      value: stats.activeSocialUsers,
      meta: `${stats.totalCommunitySpaces || 0} communities`,
      to: '/growth',
    },
  ];

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-[2.2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.22),transparent_36%),linear-gradient(180deg,#111111,#020202)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.4)] sm:p-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-4xl">
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#f7d88a]">
                Command Center
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                Welcome back, {user?.firstName || 'Admin'}.
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-[#cfcfcf]">
                The dashboard is now a cleaner control layer. Heavy workflows have been pushed into their own pages so you can scan the platform, jump into the right desk, and keep the admin side calmer.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <IdentityChip label="Email" value={user?.email || 'No email'} />
              <IdentityChip label="Phone" value={user?.phone || 'N/A'} />
              <IdentityChip label="Shimbirr ID" value={user?.shimbirrId || 'Pending'} />
              <IdentityChip label="Role" value={user?.role || 'administrator'} />
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-6">
          <SummaryCard title="Organizations" value={stats.totalOrganizations} body={`${stats.activeOrganizations || 0} active or trial`} />
          <SummaryCard title="Systems" value={stats.totalSystems} body={`${stats.pendingSystems || 0} pending setup`} />
          <SummaryCard title="Renewals" value={stats.openRenewals} body="Need commercial follow-up" />
          <SummaryCard title="Support" value={stats.openSupportCases} body="Customer queue still open" />
          <SummaryCard title="Safety" value={stats.openSocialReports} body="Moderation queue still open" />
          <SummaryCard title="OAuth clients" value={stats.oauthClients} body="Connected app access" />
        </section>

        {commandSummary ? (
          <CommandAnalyticsStrip
            summary={commandSummary}
            title="Shimbirr Command telemetry"
            description="This feed combines Shimbirr, PMS, Taban Food, and Taban Website analytics with snapshot fallback when one product is temporarily unavailable."
            onRefresh={refreshCommandAnalytics}
            refreshing={refreshingCommand}
          />
        ) : null}

        {commandSummary?.aggregateSeries?.[0]?.points?.length ? (
          <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                  Cross-Product Flow
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                  Unified activity across all connected products
                </h2>
              </div>
              <p className="text-sm text-[#b8b8b8]">
                Latest sync {commandSummary.lastUpdatedAt ? new Date(commandSummary.lastUpdatedAt).toLocaleString() : 'not recorded'}
              </p>
            </div>

            <div className="mt-6">
              <AreaTrendChart
                points={commandSummary.aggregateSeries[0].points}
                color="#FFCC00"
                accent="#FF6D00"
              />
            </div>
          </section>
        ) : null}

        <section className="grid gap-6 xl:grid-cols-[1.15fr,0.85fr]">
          <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                  Support Analytics
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                  7-day support intake trend
                </h2>
              </div>
              <p className="text-sm text-[#b8b8b8]">
                {activeSupportCases} active case{activeSupportCases === 1 ? '' : 's'} and {staleSupportCases} stale
              </p>
            </div>

            <div className="mt-6">
              <AreaTrendChart points={supportTrend} color="#FFCC00" accent="#FF6D00" />
            </div>
          </div>

          <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                Queue Breakdown
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                Support status mix
              </h2>
              <p className="mt-3 text-sm text-[#b8b8b8]">
                See where review work is piling up before you jump into the queue.
              </p>
            </div>

            <div className="mt-6">
              {supportStatusRows.length ? (
                <HorizontalMeters items={supportStatusRows} />
              ) : (
                <EmptyPanel message="No support cases have been captured yet." />
              )}
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                  Notification Analytics
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                  7-day outbound notification flow
                </h2>
              </div>
              <button
                type="button"
                onClick={() => navigate('/notifications')}
                className="rounded-full border border-[#f5b221]/28 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#f7d88a] transition hover:bg-[#f5b221]/10"
              >
                Open notifications
              </button>
            </div>

            <div className="mt-6">
              <AreaTrendChart points={notificationTrend} color="#67B7FF" accent="#2F83FF" />
            </div>
          </div>

          <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                Notification Pressure
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                Level mix and escalations
              </h2>
              <p className="mt-3 text-sm text-[#b8b8b8]">
                {notifications.length} recent notification{notifications.length === 1 ? '' : 's'} with {escalatedNotifications} warning or critical.
              </p>
            </div>

            <div className="mt-6">
              {notificationLevelRows.length ? (
                <HorizontalMeters items={notificationLevelRows} />
              ) : (
                <EmptyPanel message="No recent notification activity is available yet." />
              )}
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                Control Lanes
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                Separate pages instead of one crowded screen
              </h2>
            </div>
            <button
              type="button"
              onClick={() => navigate('/growth')}
              className="rounded-full border border-[#f5b221]/35 bg-[#f5b221] px-5 py-3 text-sm font-bold text-black transition hover:bg-[#f7d88a]"
            >
              Open growth page
            </button>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {controlLanes.map((lane) => (
              <button
                key={lane.to}
                type="button"
                onClick={() => navigate(lane.to)}
                className="rounded-[1.8rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#111111,#040404)] p-5 text-left transition hover:border-[#f5b221]/35 hover:bg-[#0b0b0b]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-lg font-bold text-white">{lane.title}</p>
                    <p className="mt-2 text-sm leading-6 text-[#b8b8b8]">{lane.body}</p>
                  </div>
                  <span className="rounded-full bg-[#f5b221] px-3 py-1 text-sm font-black text-black">
                    {lane.value}
                  </span>
                </div>
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.22em] text-[#f7d88a]">
                  {lane.meta}
                </p>
              </button>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.05fr,1.2fr,0.95fr]">
          <Panel
            title="Recent organizations"
            actionLabel="Business"
            onAction={() => navigate('/organizations')}
          >
            {recentOrganizations.length === 0 ? (
              <EmptyPanel message="No organizations have been registered yet." />
            ) : (
              recentOrganizations.map((org) => (
                <div key={org.id} className="rounded-[1.6rem] border border-[#f5b221]/10 bg-black/60 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-bold text-white">{org.name}</p>
                      <p className="mt-1 text-sm text-[#9f9f9f]">{org.ownerEmail || org.slug}</p>
                    </div>
                    <span className="rounded-full bg-[#f5b221]/12 px-3 py-1 text-xs font-bold uppercase text-[#f7d88a]">
                      {org.status}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-[#b6b6b6]">
                    <span className="rounded-full bg-[#151515] px-3 py-1">{org.planName || 'Plan pending'}</span>
                    <span className="rounded-full bg-[#151515] px-3 py-1">{org.accessType || 'Access unknown'}</span>
                  </div>
                </div>
              ))
            )}
          </Panel>

          <Panel
            title="Systems needing attention"
            actionLabel="Systems"
            onAction={() => navigate('/systems')}
          >
            {attentionSystems.length === 0 ? (
              <EmptyPanel message="No urgent systems right now." />
            ) : (
              attentionSystems.map((system) => (
                <div key={system.id} className="rounded-[1.6rem] border border-[#f5b221]/10 bg-black/60 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="font-bold text-white">{system.name}</p>
                      <p className="mt-1 text-sm text-[#9f9f9f]">
                        {system.organizationName} · {system.systemKey}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs font-bold uppercase">
                      <span className="rounded-full bg-[#151515] px-3 py-1 text-[#f7d88a]">{system.status}</span>
                      <span className="rounded-full bg-[#f5b221]/10 px-3 py-1 text-[#f5b221]">{system.licenseStatus}</span>
                    </div>
                  </div>
                  {system.healthMessage ? (
                    <p className="mt-3 rounded-[1.2rem] bg-[#111111] px-4 py-3 text-sm text-[#c9c9c9]">
                      {system.healthMessage}
                    </p>
                  ) : null}
                </div>
              ))
            )}
          </Panel>

          <Panel
            title="Recent activity"
            actionLabel="Support"
            onAction={() => navigate('/support')}
          >
            {recentActivity.length === 0 ? (
              <EmptyPanel message="No recent activity logged yet." />
            ) : (
              recentActivity.map((entry) => (
                <div key={entry.id} className="rounded-[1.6rem] border border-[#f5b221]/10 bg-black/60 p-4">
                  <p className="font-bold text-white">{entry.action}</p>
                  <p className="mt-2 text-sm text-[#b8b8b8]">
                    {(entry.actorLabel || entry.actorType || 'system')} → {entry.targetLabel || entry.targetType || 'target'}
                  </p>
                  <p className="mt-2 text-xs text-[#878787]">
                    {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
                  </p>
                </div>
              ))
            )}
          </Panel>
        </section>
      </div>
    </Layout>
  );
}

function StateCard({ message, tone = 'default' }) {
  return (
    <div
      className={[
        'flex min-h-[18rem] items-center justify-center rounded-[2rem] border p-8 text-center text-sm shadow-[0_18px_60px_rgba(0,0,0,0.34)]',
        tone === 'error'
          ? 'border-red-500/25 bg-red-950/15 text-red-200'
          : 'border-[#f5b221]/14 bg-[#070707] text-[#d8d8d8]',
      ].join(' ')}
    >
      {message}
    </div>
  );
}

function IdentityChip({ label, value }) {
  return (
    <div className="rounded-[1.5rem] border border-[#f5b221]/12 bg-[#080808] px-4 py-4">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">{label}</p>
      <p className="mt-2 text-sm font-semibold text-white">{value}</p>
    </div>
  );
}

function SummaryCard({ title, value, body }) {
  return (
    <div className="rounded-[1.8rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#111111,#040404)] p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">{title}</p>
      <p className="mt-3 text-4xl font-black tracking-[-0.04em] text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-[#b4b4b4]">{body}</p>
    </div>
  );
}

function Panel({ title, actionLabel, onAction, children }) {
  return (
    <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-xl font-black tracking-[-0.03em] text-white">{title}</h3>
        <button
          type="button"
          onClick={onAction}
          className="rounded-full border border-[#f5b221]/28 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#f7d88a] transition hover:bg-[#f5b221]/10"
        >
          {actionLabel}
        </button>
      </div>
      <div className="mt-5 space-y-4">{children}</div>
    </div>
  );
}

function EmptyPanel({ message }) {
  return (
    <div className="rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
      {message}
    </div>
  );
}
